import { useEffect, useRef, useCallback } from 'react';
import { useSimulationExecutionStore } from '@/stores/useSimulationExecutionStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { createMessageValidationService } from '@/domain/message/MessageValidationService.js';
import { createMessageDictionaryService } from '@/domain/message/MessageDictionaryService.js';
import { TestCaseVerdictService } from '@/domain/simulation/TestCaseVerdictService.js';
import { createMessageAnalysisService } from '@/domain/message/MessageAnalysisService.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';
import { PreviewMessageEncoder } from '@/domain/message/PreviewMessageEncoder.js';
import { createSimulationScheduler } from '@/domain/simulation/SimulationScheduler.js';
import { useSessionStore } from '@/stores/useSessionStore.js';
import { EvidenceService } from '@/domain/evidence/EvidenceService.js';
import { createScenarioAssetResolverService } from '@/domain/asset/ScenarioAssetResolverService.js';
import { createTemplateResolverService } from '@/domain/asset/TemplateResolverService.js';
import { createExpressionResolverService } from '@/domain/asset/ExpressionResolverService.js';
import { createParameterSetService } from '@/domain/asset/ParameterSetService.js';
import { SessionStatus } from '@k-ucap/shared';
import type {
  MessageEvent,
  SimulationTest,
  SimulationRun,
  MessageInstance,
  MessageRule,
  TestCase,
  TestCaseVerdict,
} from '@k-ucap/shared';

const createMessageEvent = (
  message: MessageInstance,
  projectId: string,
  runId: string,
  sessionId: string | null,
  validation: string
): MessageEvent => ({
  id: message.id,
  projectId,
  runId,
  messageType: message.messageType,
  standard: message.standard,
  name: message.name,
  source: message.source,
  target: message.target,
  sourceRole: message.source,
  targetRole: message.target,
  direction: message.direction,
  timestamp: message.timestamp,
  payload: message.fields,
  fields: message.fields,
  sequenceNumber: message.sequenceNumber ?? 0,
  validation,
  validationStatus: validation,
  environment: message.environment ?? 'development',
  createdAt: new Date().toISOString(),
  createdBy: 'simulation',
  metadata: {
    sessionId,
  },
});

const buildInitialRun = (
  test: SimulationTest,
  projectId: string,
  runId: string,
  sessionId: string | null,
  userId: string,
  environment: string
): SimulationRun => ({
  id: runId,
  simulationTestId: test.id,
  projectId,
  environment,
  name: `${test.name} - ${new Date().toLocaleString()}`,
  status: 'running',
  startedAt: new Date().toISOString(),
  durationMs: 0,
  generatedCount: 0,
  sentCount: 0,
  droppedCount: 0,
  passCount: 0,
  warnCount: 0,
  failCount: 0,
  messageStats: {},
  vehicleStats: {},
  executionConfig: test.executionConfig,
  metadata: {
    sessionId,
  },
  createdBy: userId,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const buildTestCaseSimulationTest = (testCase: TestCase, environment: string): SimulationTest => ({
  id: testCase.id,
  projectId: testCase.projectId,
  name: testCase.name,
  description: testCase.description,
  category: 'TEST_CASE',
  tags: testCase.tags,
  environment,
  vehicleIds: [],
  messageRules: testCase.messageRules,
  locationConfig: {
    defaultCoordinate: {
      mode: 'CENTER_RADIUS',
      centerLat: 37.389,
      centerLon: 126.647,
      radiusKm: 10,
    },
  },
  executionConfig: {
    recordMode: 'ALL',
    sampleRate: 1,
    maxConcurrentRules: 1,
    stopOnError: false,
    seed: `tc-${testCase.id}`,
  },
  createdBy: testCase.createdBy,
  createdAt: testCase.createdAt,
  updatedAt: testCase.updatedAt,
});

export const useSimulationRunner = () => {
  const {
    messageEventRepository,
    simulationRunRepository,
    messageTemplateRepository,
    parameterSetRepository,
    datasetRepository,
  } = useRepositories();
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const createSession = useSessionStore((s) => s.createSession);
  const closeSession = useSessionStore((s) => s.closeSession);
  const isRunning = useSimulationExecutionStore((s) => s.isRunning);
  const isPaused = useSimulationExecutionStore((s) => s.isPaused);
  const selectedTest = useSimulationExecutionStore((s) => s.selectedTest);
  const runId = useSimulationExecutionStore((s) => s.runId);
  const runStatus = useSimulationExecutionStore((s) => s.runStatus);
  const startExecution = useSimulationExecutionStore((s) => s.startExecution);
  const pauseExecution = useSimulationExecutionStore((s) => s.pauseExecution);
  const resumeExecution = useSimulationExecutionStore((s) => s.resumeExecution);
  const stopExecution = useSimulationExecutionStore((s) => s.stopExecution);
  const incrementRuleGenerated = useSimulationExecutionStore((s) => s.incrementRuleGenerated);
  const incrementRuleDropped = useSimulationExecutionStore((s) => s.incrementRuleDropped);
  const setRuleStatus = useSimulationExecutionStore((s) => s.setRuleStatus);
  const setElapsedMs = useSimulationExecutionStore((s) => s.setElapsedMs);
  const setRunStatus = useSimulationExecutionStore((s) => s.setRunStatus);
  const setRunError = useSimulationExecutionStore((s) => s.setRunError);

  const encoderRef = useRef(new PreviewMessageEncoder());
  const validationServiceRef = useRef(createMessageValidationService());
  const dictionaryServiceRef = useRef(createMessageDictionaryService());
  const virtualAdapterRef = useRef(getGlobalVirtualMessageAdapter());
  const schedulerRef = useRef<ReturnType<typeof createSimulationScheduler> | null>(null);
  const runRef = useRef<SimulationRun | null>(null);
  const vehicleStatsRef = useRef<SimulationRun['vehicleStats']>({});
  const sequenceRef = useRef(1);
  const sessionIdRef = useRef<string | null>(null);

  const finalizeRun = useCallback(
    async (status: 'completed' | 'stopped' | 'error') => {
      const state = useSimulationExecutionStore.getState();
      if (!runRef.current || !state.runId) return;

      const ruleStates = state.ruleStates;
      const generatedCount = Object.values(ruleStates).reduce((sum, r) => sum + r.generated, 0);
      const sentCount = Object.values(ruleStates).reduce((sum, r) => sum + r.sent, 0);
      const droppedCount = Object.values(ruleStates).reduce((sum, r) => sum + r.dropped, 0);
      const passCount = Object.values(ruleStates).reduce((sum, r) => sum + r.pass, 0);
      const warnCount = Object.values(ruleStates).reduce((sum, r) => sum + r.warn, 0);
      const failCount = Object.values(ruleStates).reduce((sum, r) => sum + r.fail, 0);

      const messageStats: SimulationRun['messageStats'] = {};
      Object.values(ruleStates).forEach((rs) => {
        messageStats[rs.messageType] = {
          generated: rs.generated,
          sent: rs.sent,
          pass: rs.pass,
          warn: rs.warn,
          fail: rs.fail,
        };
      });

      const finalRun: SimulationRun = {
        ...runRef.current,
        status,
        endedAt: new Date().toISOString(),
        durationMs: state.elapsedMs,
        generatedCount,
        sentCount,
        droppedCount,
        passCount,
        warnCount,
        failCount,
        messageStats,
        vehicleStats: vehicleStatsRef.current,
        updatedAt: new Date().toISOString(),
      };

      try {
        await simulationRunRepository.save(finalRun);
        if (sessionIdRef.current) {
          const sessionStatus =
            status === 'completed'
              ? SessionStatus.COMPLETED
              : status === 'error'
                ? SessionStatus.ERROR
                : SessionStatus.STOPPED;
          await closeSession(sessionStatus, { messageCount: generatedCount, passCount, warnCount, failCount });
          sessionIdRef.current = null;
        }
      } catch (err) {
        console.error('Failed to finalize simulation run:', err);
      }
      runRef.current = null;
      vehicleStatsRef.current = {};
    },
    [simulationRunRepository, closeSession]
  );

  const publishMessage = useCallback(
    async (message: MessageInstance, rule: MessageRule) => {
      const definition = dictionaryServiceRef.current.getMessageByType(rule.messageType);
      if (!definition) return;

      const validationResult = validationServiceRef.current.validate(definition, message.fields);
      const severity = (validationResult.severity === 'INFO' ? 'PASS' : validationResult.severity) as
        'PASS' | 'WARN' | 'FAIL';

      const currentProjectId = selectedProject?.id ?? 'demo-project';
      const currentEnvironment = selectedProject?.environment ?? 'development';
      const currentRunId = runId ?? `run-${Date.now()}`;
      const enriched: MessageInstance = {
        ...message,
        projectId: currentProjectId,
        environment: currentEnvironment,
        runId: currentRunId,
      };

      const event = createMessageEvent(enriched, currentProjectId, currentRunId, sessionIdRef.current, severity);

      incrementRuleGenerated(rule.id, severity);

      const vehicleId = String(enriched.fields.vehicleId ?? enriched.fields.uavid ?? 'unknown');
      const coords = enriched.fields.coordinates as
        { latitude: number; longitude: number; altitudeM: number } | undefined;
      const lat = coords?.latitude ?? (enriched.fields.latitude as number | undefined);
      const lon = coords?.longitude ?? (enriched.fields.longitude as number | undefined);
      const alt = coords?.altitudeM ?? (enriched.fields.altitudeM as number | undefined);
      const existing = vehicleStatsRef.current[vehicleId] ?? { messageCount: 0 };
      vehicleStatsRef.current[vehicleId] = {
        messageCount: existing.messageCount + 1,
        lastCoordinates:
          lat !== undefined && lon !== undefined
            ? { latitude: lat, longitude: lon, altitudeM: alt ?? 0 }
            : existing.lastCoordinates,
      };

      try {
        const rawBytes = encoderRef.current.encode(enriched);
        const instance = { ...enriched, rawBytes: Array.from(rawBytes) };
        await virtualAdapterRef.current.publish(instance);
      } catch {
        await virtualAdapterRef.current.publish(enriched);
      }

      const recordMode = selectedTest?.executionConfig.recordMode ?? 'SUMMARY';
      if (
        recordMode === 'ALL' ||
        (recordMode === 'SAMPLE' && sequenceRef.current % (selectedTest?.executionConfig.sampleRate ?? 10) === 0) ||
        (recordMode === 'SUMMARY' && severity !== 'PASS')
      ) {
        try {
          await messageEventRepository.save(event);
        } catch (err) {
          console.error('Simulation event save failed:', err);
          incrementRuleDropped(rule.id);
        }
      }

      // Prompt 14 Phase 3: Evidence Chain — FAIL/WARN만 자동 Evidence 생성
      const currentSession = useSessionStore.getState().currentSession;
      if (currentSession && severity !== 'PASS') {
        try {
          const evidenceItems = EvidenceService.createFromValidation(
            currentSession,
            event,
            validationResult,
            runRef.current
          );
          const addEvidence = useSessionStore.getState().addEvidence;
          await Promise.all(evidenceItems.map((item) => addEvidence(item)));
        } catch (err) {
          console.error('Failed to create evidence:', err);
        }
      }
      sequenceRef.current++;
    },
    [messageEventRepository, selectedProject, runId, incrementRuleGenerated, incrementRuleDropped, selectedTest]
  );

  const startRun = useCallback(
    async (test: SimulationTest) => {
      stopExecution();
      startExecution(test);
      sequenceRef.current = 1;
      vehicleStatsRef.current = {};
      sessionIdRef.current = null;
      setRunError(null);

      const projectId = selectedProject?.id ?? 'demo-project';
      const environment = selectedProject?.environment ?? 'development';
      const currentRunId = useSimulationExecutionStore.getState().runId ?? `run-${Date.now()}`;

      // Resolve asset references for message rules
      let resolvedTest = test;
      if (test.messageRules.some((r) => r.messageTemplateId || r.parameterSetId || r.datasetId)) {
        try {
          const resolver = createScenarioAssetResolverService({
            messageTemplateRepository,
            parameterSetRepository,
            datasetRepository,
            templateResolver: createTemplateResolverService(createExpressionResolverService()),
            parameterSetService: createParameterSetService(),
          });
          const resolvedRules = await resolver.resolveMessageRules(test.messageRules);
          resolvedTest = { ...test, messageRules: resolvedRules };
        } catch (err) {
          console.error('Failed to resolve scenario assets:', err);
          setRunError('Failed to resolve scenario assets');
        }
      }

      let sessionId: string | null = null;
      if (selectedProject && userProfile) {
        const session = await createSession({
          projectId,
          environment,
          createdBy: userProfile.uid,
          simulationTestId: test.id,
        });
        if (session) {
          sessionId = session.id;
          sessionIdRef.current = sessionId;
        }
      }

      const initialRun = buildInitialRun(
        test,
        projectId,
        currentRunId,
        sessionId,
        userProfile?.uid ?? 'anonymous',
        environment
      );
      runRef.current = initialRun;

      try {
        await simulationRunRepository.save(initialRun);
      } catch (err) {
        console.error('Failed to create simulation run:', err);
      }

      schedulerRef.current = createSimulationScheduler({
        test: resolvedTest,
        seed: resolvedTest.executionConfig.seed,
        callbacks: {
          onMessage: (ruleId, message) => {
            const rule = resolvedTest.messageRules.find((r) => r.id === ruleId);
            if (!rule) return;
            publishMessage(message, rule).catch((err) => {
              console.error('Publish message failed:', err);
              incrementRuleDropped(ruleId);
            });
          },
          onRuleStatus: (ruleId, status, error) => {
            setRuleStatus(ruleId, status, error);
          },
          onElapsed: (elapsedMs) => {
            setElapsedMs(elapsedMs);
          },
          onStatusChange: (status) => {
            setRunStatus(status);
            if (status === 'COMPLETED') {
              finalizeRun('completed').catch((err) => {
                console.error('Finalize completed run failed:', err);
              });
            }
          },
          onError: (error) => {
            setRunError(error);
          },
        },
      });

      schedulerRef.current.start();
    },
    [
      publishMessage,
      startExecution,
      stopExecution,
      setRuleStatus,
      setElapsedMs,
      setRunStatus,
      setRunError,
      incrementRuleDropped,
      finalizeRun,
      simulationRunRepository,
      messageTemplateRepository,
      parameterSetRepository,
      datasetRepository,
      selectedProject,
      userProfile,
      createSession,
    ]
  );

  const pauseRun = useCallback(() => {
    pauseExecution();
    schedulerRef.current?.pause();
  }, [pauseExecution]);

  const resumeRun = useCallback(() => {
    resumeExecution();
    schedulerRef.current?.resume();
  }, [resumeExecution]);

  const stopRun = useCallback(() => {
    schedulerRef.current?.stop();
    finalizeRun('stopped').catch((err) => {
      console.error('Finalize stopped run failed:', err);
    });
    stopExecution();
  }, [stopExecution, finalizeRun]);

  const computeVerdict = useCallback(
    async (completedRunId: string, testCase: TestCase): Promise<{ run: SimulationRun; verdict: TestCaseVerdict }> => {
      const run = await simulationRunRepository.findById(completedRunId);
      if (!run) throw new Error(`Run ${completedRunId} not found after completion`);
      const events = await messageEventRepository.findByRunId(completedRunId);
      const analysisService = createMessageAnalysisService();
      const analyses = events.map((event, index, arr) =>
        analysisService.analyze(event, { previousEvents: arr.slice(0, index) })
      );
      const verdict = TestCaseVerdictService.evaluateTestCase(testCase, run, events, analyses);
      return { run, verdict };
    },
    [simulationRunRepository, messageEventRepository]
  );

  const runTestCase = useCallback(
    async (testCase: TestCase): Promise<{ run: SimulationRun; verdict: TestCaseVerdict }> => {
      if (!selectedProject || !userProfile) {
        throw new Error('Project and user are required to run a test case');
      }
      const syntheticTest = buildTestCaseSimulationTest(testCase, selectedProject.environment);
      await startRun(syntheticTest);
      const currentRunId = useSimulationExecutionStore.getState().runId;
      if (!currentRunId) throw new Error('Run was not started');

      const currentStatus = useSimulationExecutionStore.getState().runStatus;
      if (currentStatus === 'COMPLETED' || currentStatus === 'STOPPED' || currentStatus === 'ERROR') {
        return computeVerdict(currentRunId, testCase);
      }

      return new Promise((resolve, reject) => {
        const unsubscribe = useSimulationExecutionStore.subscribe((state) => {
          if (state.runId !== currentRunId) return;
          if (state.runStatus === 'COMPLETED' || state.runStatus === 'STOPPED' || state.runStatus === 'ERROR') {
            unsubscribe();
            computeVerdict(currentRunId, testCase).then(resolve).catch(reject);
          }
        });
      });
    },
    [startRun, computeVerdict, selectedProject, userProfile]
  );

  useEffect(() => {
    return () => {
      schedulerRef.current?.stop();
    };
  }, []);

  return { startRun, pauseRun, resumeRun, stopRun, runTestCase, isRunning, isPaused, runStatus, selectedTest };
};

export default useSimulationRunner;
